import { renderTemplate } from "../templateRenderer";
import { COURIER_STATEMENT_TEMPLATE, COURIER_STATEMENT_CSS } from "./loader";
import type { PageSizeType, PageOrientation } from "../types";

export interface CourierStatementTemplateContext {
  courierName: string;
  courierPhone?: string;
  period: string;
  settledFilter: string;
  generatedAt: string;
  items: Array<{
    order_no: string;
    customer_name: string;
    assigned_at_formatted: string;
    status: string;
    is_cod: boolean;
    cod_amount: number;
    delivery_fee: number;
    net: number;
    noSettlementExpected: boolean;
    settled: boolean;
    payment_no?: string;
    payment_method_name?: string;
    payment_date_formatted?: string;
  }>;
  payments: Array<{
    payment_no: string;
    payment_date_formatted: string;
    type: string;
    amount: number;
    payment_method_name: string;
    bank_account_name?: string;
    notes?: string;
  }>;
  totals: {
    cod: number;
    fee: number;
    due: number;
    paid: number;
    paid_abs?: number;
  };
  config: {
    store_name: string;
    address: string;
    phone: string;
    currency: string;
    [key: string]: any;
  };
  pageSize?: PageSizeType;
  isForPrint?: boolean;
}

const PAGE_SIZES: Record<
  PageSizeType,
  { width: string; pad: string; font: string; orientation: PageOrientation }
> = {
  A4: {
    width: "210mm",
    pad: "12mm 15mm",
    font: "12px",
    orientation: "portrait",
  },
  A5: {
    width: "148mm",
    pad: "12mm 15mm",
    font: "12px",
    orientation: "portrait",
  },
  A3: {
    width: "297mm",
    pad: "18mm 20mm",
    font: "14px",
    orientation: "portrait",
  },
  "80mm": { width: "72mm", pad: "4mm", font: "10px", orientation: "portrait" },
};

const BASE_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>Courier Statement - {{ courierName }}</title>
  <style>{{ styles }}</style>
</head>
<body>
  {{ content | safe }}
</body>
</html>`;

function buildCssVariables(
  pageSize: PageSizeType,
  isForPrint: boolean,
): string {
  const sz = PAGE_SIZES[pageSize] || PAGE_SIZES["A4"];
  return `
    :root {
      --cs-font:       ${sz.font};
      --cs-width:      ${sz.width};
      --cs-pad:        ${isForPrint ? "0" : sz.pad};
      --cs-bg:         ${isForPrint ? "#fff" : "#edf2f7"};
      --cs-body-pad:   ${isForPrint ? "0" : "24px 16px"};
      --cs-radius:     ${isForPrint ? "0" : "8px"};
      --cs-shadow:     ${isForPrint ? "none" : "0 4px 20px rgba(0,0,0,0.06)"};
      --cs-border:     ${isForPrint ? "none" : "1px solid #e2e8f0"};
      --cs-page-size:  ${pageSize};
      --cs-orientation: ${sz.orientation};
    }
  `;
}

export function renderCourierStatement(
  ctx: CourierStatementTemplateContext,
): string {
  const { pageSize = "A4", isForPrint = false } = ctx;
  const currency = ctx.config?.currency || "LKR";

  const contentContext = {
    ...ctx,
    currency,
    totals: {
      ...ctx.totals,
      paid_abs: Math.abs(ctx.totals.paid),
    },
    logoUrl: ctx.config?.logo_url ? `${ctx.config.logo_url}` : "",
  };

  const content = renderTemplate(COURIER_STATEMENT_TEMPLATE, contentContext);
  const styles =
    buildCssVariables(pageSize, isForPrint) + "\n" + COURIER_STATEMENT_CSS;

  return renderTemplate(BASE_TEMPLATE, {
    title: `Courier Statement - ${ctx.courierName}`,
    styles,
    content,
  });
}
